import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from '@/i18n';
import type { TableColumn, BulkActionItem } from '@/components/ui/tables/Table';
import type { ActionMenuItem } from '@/components/ui/menus';
import { MonthlyCutProgressCell } from '@/components/features/monthlyCuts';
import {
  monthlyCutsService,
  monthlyCutsConceptGenerationService,
  CONCEPT_GENERATION_ACTIVE_CONFLICT_CODE,
} from '@/api/domains/attendance';
import type {
  MonthlyCutManagementItem,
  MonthlyCutsCounters,
  PayrollSubmissionSummary,
} from '@/api/domains/attendance';
import { useUI } from '@/hooks/ui/useUI';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult, PaginatedListSort } from '@/hooks/ui/usePaginatedList';
import { useHoldingCompanies } from '@/api/domains/catalog/holding/hooks/useHoldingCompanies';
import { monthlyCutsYearsService, mapMonthlyCutYearItemToOption } from '@/api/domains/catalog/monthlyCutsYears';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { usePayrollSubmissionAvailability } from '@/hooks/attendance/monthlyCuts/usePayrollSubmissionAvailability';
import { ROUTE_DEFINITIONS } from '@/routes';
import { getMonthOptions } from '@/utils/dateRange';
import type { SimpleFilter } from '@/components/ui/filters/FilterDropdown';


export interface MonthlyCutsFilters {
  companyId: string | null;
  year: string | null;
  month: string | null;
  state: string | null;
}

const initialFilters: MonthlyCutsFilters = {
  companyId: null,
  year: null,
  month: null,
  state: null,
};

export interface UseMonthlyCutsListResult {
  rows: MonthlyCutManagementItem[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  columns: TableColumn<MonthlyCutManagementItem>[];
  onSearch: (term: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  searchText: string;
  sort: PaginatedListSort | undefined;
  loading: boolean;
  refetch: () => void;
  counters: MonthlyCutsCounters;
  countersLoading: boolean;
  filterConfig: SimpleFilter[];
  filters: Record<string, string | string[] | undefined>;
  selectedFilters: MonthlyCutsFilters;
  onChangeFilter: (key: string, value: string | string[]) => void;
  canView: boolean;
  canEdit: boolean;
  permissionsLoaded: boolean;
  // Selección + acciones masivas (generación de conceptos — ASIS-4969)
  selectable: boolean;
  bulkActions: BulkActionItem[];
  onSelectionChange: (selected: MonthlyCutManagementItem[]) => void;
  // Key para forzar el reinicio de la selección de la tabla tras un envío (remonta la tabla).
  tableSelectionKey: string;
  // Envío a remuneraciones (ASIS-4971 / FE-012)
  isSendPayrollModalOpen: boolean;
  sendPayrollCuts: MonthlyCutManagementItem[];
  onSendPayrollModalClose: () => void;
  onSendPayrollSuccess: (summary: PayrollSubmissionSummary) => void;
}

// Estado de corte que impide editar (espejo de la regla backend CONFLICT_MONTHLY_CUT_CLOSED).
const CLOSED_STATE = 'Cerrado';

export function useMonthlyCutsList(): UseMonthlyCutsListResult {
  const { showApiError, showSuccess } = useUI();
  const { t: translate } = useTranslation();
  const navigate = useNavigate();

  
  const [sessionGenerations, setSessionGenerations] =
    useState<Map<number, { key: number; active: boolean }>>(new Map());
  const [selectedRows, setSelectedRows] = useState<MonthlyCutManagementItem[]>([]);

  // Envío a remuneraciones (ASIS-4971): estado del diálogo y snapshot de cortes a enviar.
  const [isSendPayrollModalOpen, setIsSendPayrollModalOpen] = useState(false);
  const [sendPayrollCuts, setSendPayrollCuts] = useState<MonthlyCutManagementItem[]>([]);
  // Token para reiniciar la selección visual de la tabla (remonta la tabla al incrementarse).
  const [selectionVersion, setSelectionVersion] = useState(0);

  const { isLoaded: permissionsLoaded, canView, canEdit } = usePagePermissions();

  // Disponibilidad de la integración de remuneraciones. Solo se consulta con permiso de edición.
  // Con permiso pero sin integración activa, la acción se muestra deshabilitada con tooltip.
  const { available: payrollAvailable, isLoaded: payrollAvailabilityLoaded } =
    usePayrollSubmissionAvailability(permissionsLoaded && canEdit);

  // Redirect if permissions loaded and user cannot view
  useEffect(() => {
    if (permissionsLoaded && !canView) {
      void navigate(ROUTE_DEFINITIONS.DASHBOARD_ASISTENCIA.path, { replace: true });
    }
  }, [permissionsLoaded, canView, navigate]);

  // Catalogs
  const { companies } = useHoldingCompanies();
  const companyOptions = useMemo(
    () => companies.map((c) => ({ value: String(c.id), label: c.name })),
    [companies],
  );

  // Counters state
  const [counters, setCounters] = useState<MonthlyCutsCounters>({ total: 0, open: 0, closed: 0 });
  const [countersLoading, setCountersLoading] = useState(true);

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<MonthlyCutsFilters>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<MonthlyCutManagementItem> | null> => {
      const response = await monthlyCutsService.listManagement(
        {
          page: params.page,
          pageSize: params.pageSize,
          searchText: params.searchText,
          sort: params.sort
            ? { key: params.sort.key, direction: params.sort.direction.toLowerCase() as 'asc' | 'desc' }
            : undefined,
          filterCompanyId: params.filters?.companyId,
          year: params.filters?.year,
          month: params.filters?.month,
          state: params.filters?.state,
        },
        signal,
      );

      if (!response.success || !response.data) {
        if (response.error?.code === 'ABORTED') return null;
        showApiError(response, translate('attendance:monthlyCutsList.messages.loadError'));
        return null;
      }

      return { items: response.data.items, totalRecords: response.data.totalRecords };
    },
    [showApiError, translate],
  );

  // Single source of truth for filter state: usePaginatedList owns `filters`.
  // The table re-fetches on change; counters and year options derive from the same `filters`.
  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
    sort,
    debouncedSearchText: searchText,
    refetch: refetchTable,
    filters,
    setFilters,
  } = usePaginatedList<MonthlyCutManagementItem, MonthlyCutsFilters>({
    fetchFn,
    initialFilters,
    initialSort: { key: 'monthlyCutId', direction: 'DESC' },
    orderByMap: {
      id: 'monthlyCutId',
      companyName: 'companyName',
      year: 'year',
      month: 'month',
      range: 'startDate',
      process: 'process',
      state: 'state',
    },
    deduplicate: false,
    enabled: permissionsLoaded && canView,
  });

  // Year options — reload when the company filter changes (derived from the single filter source)
  const [yearOptions, setYearOptions] = useState<{ value: string; label: string }[]>([]);
  useEffect(() => {
    const controller = new AbortController();
    void monthlyCutsYearsService
      .getYears(filters.companyId, controller.signal)
      .then((response) => {
        if (response.error?.code === 'ABORTED') return;
        setYearOptions(response.data?.map(mapMonthlyCutYearItemToOption) ?? []);
      });
    return () => { controller.abort(); };
  }, [filters.companyId]);

  // Counters load: derives directly from `filters`; accepts optional signal.
  const loadCounters = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (!permissionsLoaded || !canView) return;
      setCountersLoading(true);
      try {
        const response = await monthlyCutsService.getCounters(
          {
            filterCompanyId: filters.companyId,
            year: filters.year,
            month: filters.month,
            state: filters.state,
          },
          signal,
        );
        if (response.error?.code === 'ABORTED') return;
        if (!response.success || !response.data) {
          showApiError(response, translate('attendance:monthlyCutsList.messages.countersError'));
          return;
        }
        setCounters(response.data);
      } finally {
        setCountersLoading(false);
      }
    },
    [
      permissionsLoaded,
      canView,
      filters.companyId,
      filters.year,
      filters.month,
      filters.state,
      showApiError,
      translate,
    ],
  );

  // Re-fetch counters whenever loadCounters reference changes
  // (i.e., when permissions or company/year/month/state filters change)
  useEffect(() => {
    const controller = new AbortController();
    void loadCounters(controller.signal);
    return () => { controller.abort(); };
  }, [loadCounters]);

  // Combined refetch: refreshes both table rows and counters
  const refetch = useCallback(() => {
    refetchTable();
    void loadCounters();
  }, [refetchTable, loadCounters]);

  const onChangeFilter = useCallback(
    (key: string, value: string | string[]) => {
      const strValue = Array.isArray(value) ? (value[0] ?? '') : value;
      setFilters((prev) => {
        const next = { ...prev, [key]: strValue || null };
        // Year options depend on the selected company; reset the dependent year filter
        // when the company changes so a stale year is not sent to the backend.
        if (key === 'companyId') {
          next.year = null;
        }
        return next;
      });
    },
    [setFilters],
  );

  const handleEdit = useCallback(
    (row: MonthlyCutManagementItem) => {
      void navigate(
        ROUTE_DEFINITIONS.CORTE_MENSUAL_EDITAR.path.replace(':id', String(row.id)),
      );
    },
    [navigate],
  );

  // Encola la generación de conceptos para uno o varios cortes. Al recibir 202, marca los
  // cortes efectivamente encolados como activos para que su fila muestre la barra de progreso.
  // Los cortes que ya estaban en curso (alreadyActiveCutIds) se informan sin bloquear el resto.
  const handleGenerateConcepts = useCallback(
    async (monthlyCutIds: number[]): Promise<void> => {
      if (monthlyCutIds.length === 0) return;

      const response = await monthlyCutsConceptGenerationService.start({ monthlyCutIds });

      if (!response.success || !response.data) {
        showApiError(
          response,
          translate('attendance:monthlyCutsList.conceptGeneration.messages.startError'),
        );
        // 409: todos los cortes solicitados ya están generando. Refrescamos el listado para que sus
        // filas muestren la barra (estado autoritativo conceptGenerationActive), en vez de dejar
        // solo el toast de error.
        if (response.error?.code === CONCEPT_GENERATION_ACTIVE_CONFLICT_CODE) {
          refetch();
        }
        return;
      }

      const { queuedCutIds, alreadyActiveCutIds } = response.data;
      if (queuedCutIds.length > 0) {
        // Marca cada corte encolado como activo en sesión e incrementa su `key` para (re)arrancar
        // la barra, incluso si ya se había generado antes en esta misma sesión (re-disparo).
        setSessionGenerations((previous) => {
          const next = new Map(previous);
          queuedCutIds.forEach((id) => {
            const prev = next.get(id);
            next.set(id, { key: (prev?.key ?? 0) + 1, active: true });
          });
          return next;
        });
      }

      // Si el lote traía cortes ya en curso, se informa (mensaje parcial) y se refresca el listado
      // para que esas filas muestren su barra con el estado autoritativo. Si no, éxito normal.
      if (alreadyActiveCutIds.length > 0) {
        showSuccess(
          translate('attendance:monthlyCutsList.conceptGeneration.messages.startPartialAlreadyActive', {
            queued: queuedCutIds.length,
            skipped: alreadyActiveCutIds.length,
          }),
        );
        refetch();
      } else if (queuedCutIds.length > 0) {
        showSuccess(translate('attendance:monthlyCutsList.conceptGeneration.messages.startSuccess'));
      }
      // Limpia la selección tras encolar para evitar reenvíos accidentales.
      setSelectedRows([]);
    },
    [showApiError, showSuccess, translate, refetch],
  );

  const onSelectionChange = useCallback(
    (selected: MonthlyCutManagementItem[]) => { setSelectedRows(selected); },
    [],
  );

  // Abre el diálogo de envío a remuneraciones con un snapshot de los cortes (fila o selección).
  const openSendPayrollModal = useCallback((cuts: MonthlyCutManagementItem[]) => {
    setSendPayrollCuts(cuts);
    setIsSendPayrollModalOpen(true);
  }, []);

  const onSendPayrollModalClose = useCallback(() => {
    setIsSendPayrollModalOpen(false);
  }, []);

  // Tras un envío: limpia la selección (remonta la tabla) y refresca listado + contadores. No
  // cierra el diálogo — la fase de resultado permanece visible hasta que el usuario la cierra.
  const onSendPayrollSuccess = useCallback(
    (_summary: PayrollSubmissionSummary) => {
      setSelectedRows([]);
      setSelectionVersion((version) => version + 1);
      refetch();
    },
    [refetch],
  );

  const tableSelectionKey = useMemo(() => String(selectionVersion), [selectionVersion]);

  
  const handleGenerationFinished = useCallback(
    (monthlyCutId: number) => {
      setSessionGenerations((previous) => {
        const prev = previous.get(monthlyCutId);
        if (!prev || !prev.active) return previous;
        const next = new Map(previous);
        next.set(monthlyCutId, { key: prev.key, active: false });
        return next;
      });
      refetch();
    },
    [refetch],
  );

  const monthOptions = useMemo(() => getMonthOptions(translate), [translate]);

  const stateOptions = useMemo(
    () => [
      { value: 'Abierto', label: translate('attendance:monthlyCutsList.filters.stateOpen') },
      { value: 'Cerrado', label: translate('attendance:monthlyCutsList.filters.stateClosed') },
    ],
    [translate],
  );

  const allOption = useMemo(
    () => ({ value: '', label: translate('attendance:monthlyCutsList.filters.all') }),
    [translate],
  );

  const filterConfig: SimpleFilter[] = useMemo(
    () => [
      {
        key: 'companyId',
        label: translate('attendance:monthlyCutsList.filters.company'),
        options: [allOption, ...companyOptions],
      },
      {
        key: 'year',
        label: translate('attendance:monthlyCutsList.filters.year'),
        options: [allOption, ...yearOptions],
      },
      {
        key: 'month',
        label: translate('attendance:monthlyCutsList.filters.month'),
        options: [allOption, ...monthOptions],
      },
      {
        key: 'state',
        label: translate('attendance:monthlyCutsList.filters.state'),
        options: [allOption, ...stateOptions],
      },
    ],
    [translate, allOption, companyOptions, yearOptions, monthOptions, stateOptions],
  );

  // Values for FilterDropdown derived from the single filter source (null → '' = "Todos")
  const filterValues = useMemo<Record<string, string>>(
    () => ({
      companyId: filters.companyId ?? '',
      year: filters.year ?? '',
      month: filters.month ?? '',
      state: filters.state ?? '',
    }),
    [filters],
  );

  // Acciones masivas: "Generar conceptos" solo visible con permiso WRITE sobre CORTE_MENSUAL.
  // Se omiten los cortes Cerrados de la selección (el backend igual los reporta como skipped).
  // "Enviar a remuneraciones" solo se ofrece con integración activa: BulkActionItem no soporta
  // estado deshabilitado/tooltip, por lo que la acción no aparece cuando no hay integración.
  const bulkActions: BulkActionItem[] = useMemo(() => {
    if (!canEdit) return [];
    const actions: BulkActionItem[] = [
      {
        label: translate('attendance:monthlyCutsList.conceptGeneration.menu.generate'),
        icon: 'cog',
        onClick: () => {
          const openCutIds = selectedRows
            .filter((row) => row.state !== CLOSED_STATE)
            .map((row) => row.id);
          void handleGenerateConcepts(openCutIds);
        },
      },
    ];
    if (payrollAvailable) {
      actions.push({
        label: translate('attendance:monthlyCutsSendToPayroll.bulk.send'),
        icon: 'export',
        onClick: () => { openSendPayrollModal(selectedRows); },
      });
    }
    return actions;
  }, [
    canEdit,
    translate,
    selectedRows,
    handleGenerateConcepts,
    payrollAvailable,
    openSendPayrollModal,
  ]);

  const columns: TableColumn<MonthlyCutManagementItem>[] = useMemo(
    () => [
      {
        key: 'id',
        header: translate('attendance:monthlyCutsList.fields.id'),
        sortable: true,
        render: (row) => row.id,
      },
      {
        key: 'companyName',
        header: translate('attendance:monthlyCutsList.fields.company'),
        sortable: true,
        render: (row) => row.companyName,
      },
      {
        key: 'year',
        header: translate('attendance:monthlyCutsList.fields.year'),
        sortable: true,
        render: (row) => row.year,
      },
      {
        key: 'month',
        header: translate('attendance:monthlyCutsList.fields.month'),
        sortable: true,
        render: (row) => row.month,
      },
      {
        key: 'range',
        header: translate('attendance:monthlyCutsList.fields.range'),
        sortable: true,
        render: (row) => row.range,
      },
      {
        key: 'process',
        header: translate('attendance:monthlyCutsList.fields.process'),
        sortable: true,
        render: (row) => row.process ?? '-',
      },
      {
        key: 'state',
        header: translate('attendance:monthlyCutsList.fields.state'),
        sortable: true,
        render: (row) => row.state,
      },
      {
        key: 'actions',
        header: '',
        sortable: false,
        // Sin ancho explícito: usa el ancho responsivo del resto de columnas (no fuerza scroll
        // horizontal en notebooks). La barra llena el ancho de la celda y queda centrada; el menú
        // "…" se centra igual que en el resto de la app.
        align: 'center',
        overflowStrategy: 'wrap',
        render: (row) => {
          // Las acciones solo se ofrecen con permiso de edición y si el corte está Abierto.
          const canActOnRow = canEdit && row.state !== CLOSED_STATE;
          const menuItems: ActionMenuItem[] = canActOnRow
            ? [
              {
                id: 'edit',
                label: translate('common:actions.edit'),
                icon: 'edit',
                onClick: () => handleEdit(row),
              },
              {
                // "Generar conceptos" solo visible con permiso WRITE sobre CORTE_MENSUAL.
                id: 'generateConcepts',
                label: translate('attendance:monthlyCutsList.conceptGeneration.menu.generate'),
                icon: 'cog',
                onClick: () => { void handleGenerateConcepts([row.id]); },
              },
              {
                // "Enviar a remuneraciones": con permiso pero sin integración activa se muestra
                // deshabilitada con un tooltip informativo (el tooltip solo se define una vez
                // resuelta la disponibilidad, para no informar "sin integración" durante la carga).
                id: 'sendToPayroll',
                label: translate('attendance:monthlyCutsSendToPayroll.menu.send'),
                icon: 'export',
                onClick: () => { openSendPayrollModal([row]); },
                disabled: !payrollAvailable,
                tooltip: payrollAvailabilityLoaded && !payrollAvailable
                  ? translate('attendance:monthlyCutsSendToPayroll.menu.notAvailableTooltip')
                  : undefined,
              },
            ]
            : [];
          // La celda decide entre barra de progreso, mensaje de error y menú "…". La barra arranca
          // por la señal de sesión (recién disparada aquí) o por el estado autoritativo del listado
          // (generación en curso al cargar / de otro usuario / tras recargar).
          const session = sessionGenerations.get(row.id);
          return React.createElement(MonthlyCutProgressCell, {
            monthlyCutId: row.id,
            menuItems,
            sessionActive: session?.active ?? false,
            generationKey: session?.key ?? 0,
            initiallyGenerating: row.conceptGenerationActive,
            initialProgressPercentage: row.conceptGenerationProgress,
            onFinished: () => handleGenerationFinished(row.id),
          });
        },
      },
    ],
    [
      translate,
      canEdit,
      handleEdit,
      handleGenerateConcepts,
      sessionGenerations,
      handleGenerationFinished,
      payrollAvailable,
      payrollAvailabilityLoaded,
      openSendPayrollModal,
    ],
  );

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    onSearch,
    onSortChange,
    searchText,
    sort,
    loading,
    refetch,
    counters,
    countersLoading,
    filterConfig,
    filters: filterValues,
    selectedFilters: filters,
    onChangeFilter,
    canView,
    canEdit,
    permissionsLoaded,
    selectable: canEdit,
    bulkActions,
    onSelectionChange,
    tableSelectionKey,
    isSendPayrollModalOpen,
    sendPayrollCuts,
    onSendPayrollModalClose,
    onSendPayrollSuccess,
  };
}

export default useMonthlyCutsList;
